import { useActor, useSelector } from "@xstate/react";
import {
  TicketScannerApiRequirements,
  TicketScannerMachine,
} from "./TicketScannerMachine";
import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonSpinner,
  useIonAlert,
} from "@ionic/react";
import { PageContainer } from "@greeter/matter";
import { useEffect, useMemo } from "react";
import "./scanner.css";
import { classNames, isType } from "@greeter/util";
import { Card } from "@greeter/matter-v2";
import { useFetchActorLoading } from "@greeter/xstate-util";
import { Ticket, TicketEvent, TicketId, VenueTicket } from "@greeter/core";
import {
  IconAlertSquare,
  IconCalendar,
  IconChecks,
  IconQrcode,
  IconRosetteDiscountCheck,
} from "@tabler/icons-react";
import { TicketOrderCard } from "../TicketOverviewPage";
import { ApiError } from "@greeter/api";
import { Cam } from "./Cam";

import css from "./TicketScannerPage.module.scss";

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function toTicketIdSet(m: Map<TicketId, boolean>): Set<TicketId> {
  const s = new Set<TicketId>();
  for (const [k, v] of m) {
    if (v) s.add(k);
  }
  return s;
}

type TicketDetailsProps = {
  ticket: VenueTicket;
  tickets: Array<VenueTicket>;

  loading?: boolean;
  loadingTickets?: Map<TicketId, boolean>;

  status: "valid" | "already-checked-in";
  ticketEvent?: Pick<TicketEvent, "eventPeriod">;
  ticketSummary: { total: number; checkedIn: number };
  onScanNext?: () => void;
  onCheckAllIn?: () => void;
};
function TicketDetails({
  status,
  loading,
  loadingTickets,
  ticket,
  tickets,
  ticketEvent,
  ticketSummary,
  onScanNext,
  onCheckAllIn,
}: TicketDetailsProps) {
  const anyLoadingTickets = useMemo(
    () => Array.from(loadingTickets?.values() ?? []).some((b) => b),
    [loadingTickets]
  );

  return (
    <Card variant="dark-blue" borderless className={css.TicketDetails}>
      <Card.Content className={css.Content}>
        {status === "valid" && <TicketDetails.ValidLabel />}
        {status === "already-checked-in" && <TicketDetails.InvalidLabel />}
        <div>
          <h1 style={{ fontSize: 24, marginTop: 5, marginBottom: 5 }}>
            {ticket.customerName}
          </h1>
          <span
            style={{
              color: "#94A3B8",
            }}
          >
            {ticket.ticketEventTitle}
          </span>
          {ticketEvent ? (
            <div className={css.CalendarTag}>
              <IconCalendar className={css.CalendarIcon} />
              <span style={{ color: "#6B7280" }}>
                {isType(ticketEvent.eventPeriod, "once") &&
                  dateFormatter.format(new Date(ticketEvent.eventPeriod.date))}
              </span>
            </div>
          ) : (
            <IonSkeletonText style={{ width: "100%" }} />
          )}
        </div>

        <TicketOrderCard.ScanProgress
          total={ticketSummary.total}
          current={ticketSummary.checkedIn}
          tickets={tickets}
          loading={toTicketIdSet(loadingTickets ?? new Map())}
          loadingAll={loading}
        />
        <TicketDetails.OrderDetails ticket={ticket} />
        <TicketDetails.ButtonBar>
          <TicketDetails.ScanNextButton onClick={onScanNext} />
          <TicketDetails.CheckAllInButton
            onClick={onCheckAllIn}
            loading={anyLoadingTickets}
          />
        </TicketDetails.ButtonBar>
      </Card.Content>
    </Card>
  );
}

TicketDetails.OrderDetails = function ({ ticket }: { ticket: Ticket }) {
  return (
    <div className={css.OrderDetails}>
      <div className={css.Item}>
        <span>Ordre ID</span>
        <span>#{ticket.id.split("-").at(-1)}</span>
      </div>
      <div className={css.Item}>
        <span>Billettype</span>
        <span>Normal billet</span>
      </div>
    </div>
  );
};

TicketDetails.ScanNextButton = function (props: { onClick?: () => void }) {
  return (
    <Card variant="bright-green" onClick={props.onClick}>
      <Card.Content className="flex-row flex-center">
        <IconQrcode height={20} />
        Scan næste
      </Card.Content>
    </Card>
  );
};

TicketDetails.CheckAllInButton = function (props: {
  onClick?: () => void;
  loading?: boolean;
}) {
  return (
    <Card variant="bright-green-hollow" onClick={props.onClick}>
      <Card.Content className="flex-row flex-center">
        {props.loading ? (
          <IonSpinner style={{ height: 25 }} />
        ) : (
          <IconChecks style={{ height: 25 }} />
        )}
        Tjek alle ind
      </Card.Content>
    </Card>
  );
};

TicketDetails.ButtonBar = function (props: React.PropsWithChildren) {
  return <div className={css.ButtonBar} {...props} />;
};

TicketDetails.ValidLabel = function () {
  return (
    <div {...classNames(css.TicketDetailsLabel, css.Valid)}>
      <IconRosetteDiscountCheck
        style={{
          fill: "#2ECC71",
          border: "none",
          width: 30,
          height: 30,
          stroke: "#161B22",
        }}
      />
      <span style={{ color: "#2ECC71", fontSize: 28, fontWeight: "bold" }}>
        Gyldig
      </span>
    </div>
  );
};
TicketDetails.InvalidLabel = function () {
  return (
    <div {...classNames(css.TicketDetailsLabel, css.Invalid)}>
      <IconAlertSquare
        style={{ fill: "#CC2E2E", width: 20, height: 20, stroke: "#161B22" }}
      />
      <span style={{ color: "#9B3535", fontSize: 14, fontWeight: "bold" }}>
        UGYLDIG BILLET - ER INDLØST
      </span>
    </div>
  );
};

export type TicketScannerPageProps = {
  api: TicketScannerApiRequirements;
};

export function TicketScannerPage(props: TicketScannerPageProps) {
  const [snap, send, m] = useActor(TicketScannerMachine, {
    input: { api: props.api },
  });
  const api = useSelector(m, (s) => s.context.api);
  const fetchTicketLoading = useFetchActorLoading(api.fetchTicketRef);
  const ticketEvent = useSelector(
    api.fetchTicketEventRef,
    (s) => s.context.data
  );
  const ticketSummary = useSelector(m, (s) => s.context.ticketSummary);

  useEffect(() => {
    document.addEventListener("keydown", (ev) => {
      if (ev.code === "KeyQ") {
        send({
          type: "scanSuccess",
          data: "89865cd7-eb1e-4ff5-8a01-bd511498172f" as TicketId,
          // data: "0d615178-f9f9-11f0-ac23-07c367b99e0b" as TicketId,
        });
      }
      if (ev.code === "KeyC") {
        send({ type: "scanNext" });
        document
          .querySelector(`.${css.Reader}`)
          ?.scrollIntoView({ block: "start" });
      }
    });
  }, []);

  const ticket = useSelector(m, (s) => s.context.ticket);
  const ticketError = useSelector(m, (s) => s.context.ticketError);

  const tickets = useSelector(m, (s) => s.context.tickets);
  const ticketsLoading = useFetchActorLoading(api.fetchTicketsRef);

  const status = useSelector(m, (s) => s.context.status);

  useEffect(() => {
    const s2 = m.on("scanning", () => {});
    return () => {
      s2.unsubscribe();
    };
  }, [m]);

  const saveTicketsLoading = useFetchActorLoading(
    snap.context.api.saveTicketsRef
  );
  const loadingTickets = useSelector(snap.context.api.saveTicketsRef, (s) => {
    return s.context.input?.[0]?.reduce((acc, curr) => {
      acc.set(curr.id, true);
      return acc;
    }, new Map<TicketId, boolean>());
  });

  const [alert, dismissAlert] = useIonAlert();

  return (
    <IonPage>
      <IonContent
        style={{
          "--background": "#0f1720",
        }}
      >
        <PageContainer
          style={{
            backgroundColor: "#0F1720",
            height: "100%",
            paddingBottom: "unset",
          }}
        >
          <div
            {...classNames(
              css.TicketScannerPage,
              !snap.context.ticket && snap.matches("scanning")
                ? css.FullScreenCamera
                : snap.matches("scanning")
                ? css.FocusCamera
                : css.FocusTickets
            )}
          >
            <div className={css.ReaderContainer}>
              <div
                id="reader"
                style={{ width: "100%", height: "33dvh", position: "relative" }}
              />
              <div
                {...classNames(
                  css.ReaderWrapper,
                  snap.matches("scanning") && css.Scanning
                )}
              >
                <Cam
                  {...classNames(css.Reader)}
                  onDecoded={async (b) => {
                    send({ type: "scanSuccess", data: b as TicketId });
                  }}
                  scanning={snap.matches("scanning")}
                />
                {snap.matches("scanned") && (
                  <Card
                    className={css.ScanNextButton}
                    variant="green"
                    act="button"
                    onClick={() => {
                      send({ type: "scanNext" });
                      document
                        .querySelector(`#reader`)
                        ?.scrollIntoView({ block: "start" });
                    }}
                  >
                    <Card.Content className="flex-row flex-center">
                      <IconQrcode />
                      Scan næste
                    </Card.Content>
                  </Card>
                )}
              </div>
            </div>
            <div className={css.Ticket}>
              {!ticket && !ticketError && !fetchTicketLoading && (
                <span>Scan din første billet og se den her!</span>
              )}
              {(ApiError.isNotFoundError(ticketError) ||
                ApiError.isBadRequestError(ticketError)) &&
                !fetchTicketLoading && <span>Billetten eksisterer ikke</span>}
              {fetchTicketLoading && <span>Henter...</span>}
              {!ticketError && ticket && !fetchTicketLoading && (
                <TicketDetails
                  status={status ?? "valid"}
                  ticket={ticket}
                  tickets={Array.from(tickets?.values() ?? [])}
                  ticketEvent={ticketEvent}
                  ticketSummary={ticketSummary ?? { total: 0, checkedIn: 0 }}
                  loading={ticketsLoading}
                  loadingTickets={
                    saveTicketsLoading ? loadingTickets ?? new Map() : new Map()
                  }
                  onCheckAllIn={() => {
                    alert({
                      mode: "ios",
                      header: "Er du sikker?",
                      message:
                        "Det vil tjekke alle billetter ind UDEN at de bliver tjekket med en scanner.",
                      buttons: [
                        { text: "Nej" },
                        {
                          text: "Ja, tjek alle ind",
                          handler: () => {
                            send({ type: "checkAllIn" });
                          },
                        },
                      ],
                    });
                  }}
                  onScanNext={() => {
                    send({ type: "scanNext" });
                    document
                      .querySelector(`.${css.Reader}`)
                      ?.scrollIntoView({ block: "start" });
                  }}
                />
              )}
            </div>
          </div>
        </PageContainer>
      </IonContent>
    </IonPage>
  );
}
